import { useId } from "react";
import type { PersonalizedFertilizerPdpResponse } from "@halm-club/halm-shared";
import { Modal } from "../../../../shared/pdp/components/Modal";
import { formatMoney } from "../../../../shared/pdp/utils/pricing";
import "./SoilAnalysisComparisonModal.css";

type SoilAnalysisVariant =
  PersonalizedFertilizerPdpResponse["soilAnalysisVariants"][number];

type SoilAnalysisComparisonModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectAi: () => void;
  soilAnalysisVariants: SoilAnalysisVariant[];
};

const getPriceLabel = (variant?: SoilAnalysisVariant) => {
  if (!variant) return "–";

  return variant.priceInCents === 0
    ? "inklusive"
    : formatMoney(variant.priceInCents);
};

export function SoilAnalysisComparisonModal({
  isOpen,
  onClose,
  onSelectAi,
  soilAnalysisVariants,
}: SoilAnalysisComparisonModalProps) {
  const headingId = useId();
  const labVariant = soilAnalysisVariants.find(
    (variant) => variant.type === "LAB",
  );
  const aiVariant = soilAnalysisVariants.find(
    (variant) => variant.type === "AI",
  );

  const selectAiAndClose = () => {
    onSelectAi();
    onClose();
  };

  return (
    <Modal
      className="personalized-fertilizer-pdp__analysis-modal"
      dataTest="fertilizer-analysis-modal"
      isOpen={isOpen}
      labelledBy={headingId}
      onClose={onClose}
      overlayClassName="personalized-fertilizer-pdp__analysis-modal-overlay"
    >
      <div className="personalized-fertilizer-pdp__analysis-modal-handle-wrapper">
        <span className="personalized-fertilizer-pdp__analysis-modal-handle" />
      </div>

      <div className="personalized-fertilizer-pdp__analysis-modal-content">
        <header className="personalized-fertilizer-pdp__analysis-modal-header">
          <h2
            className="personalized-fertilizer-pdp__analysis-modal-heading"
            id={headingId}
          >
            Ohne Bodenanalyse starten?
          </h2>
          <button
            aria-label="Dialog schließen"
            className="personalized-fertilizer-pdp__analysis-modal-close"
            onClick={onClose}
            type="button"
          >
            <span aria-hidden="true" className="material-symbols-rounded">
              close
            </span>
          </button>
        </header>

        <div className="personalized-fertilizer-pdp__analysis-modal-options">
          <div className="personalized-fertilizer-pdp__analysis-modal-option">
            <span
              aria-hidden="true"
              className="material-symbols-rounded personalized-fertilizer-pdp__analysis-modal-option-icon"
            >
              check_circle
            </span>
            <p className="personalized-fertilizer-pdp__analysis-modal-option-copy">
              <strong>
                Bodenanalyse im Labor ({getPriceLabel(labVariant)}):
              </strong>{" "}
              <span>
                Misst deine Bodenwerte anhand deiner echten Bodenprobe. Höchste
                Präzision.
              </span>
            </p>
          </div>

          <div className="personalized-fertilizer-pdp__analysis-modal-option">
            <span
              aria-hidden="true"
              className="material-symbols-rounded personalized-fertilizer-pdp__analysis-modal-option-icon"
            >
              electric_bolt
            </span>
            <p className="personalized-fertilizer-pdp__analysis-modal-option-copy">
              <strong>KI-Analyse ({getPriceLabel(aiVariant)}):</strong>{" "}
              <span>
                Schätzt deine Bodenwerte auf Basis regionaler Bodendaten und
                Vergleichswerten aus über 10k Bodenproben. Du erhältst deinen
                Dünger sofort.
              </span>
            </p>
          </div>
        </div>

        <div className="personalized-fertilizer-pdp__analysis-modal-recommendation">
          <strong>Unsere Empfehlung</strong>
          <p>
            Wenn du mit der Düngung etwas warten kannst, ist die Laboranalyse
            die bessere Grundlage - denn selbst benachbarte Rasen können stark
            unterschiedliche Bodenwerte haben. Du kannst auch mit KI starten und
            spätere Lieferungen auf echte Laborwerte abstimmen lassen.
          </p>
        </div>

        <div className="personalized-fertilizer-pdp__analysis-modal-actions">
          <button
            className="personalized-fertilizer-pdp__analysis-modal-button personalized-fertilizer-pdp__analysis-modal-button--primary"
            data-test="fertilizer-keep-lab-analysis"
            onClick={onClose}
            type="button"
          >
            Bei Bodenanalyse bleiben
          </button>
          <button
            className="personalized-fertilizer-pdp__analysis-modal-button personalized-fertilizer-pdp__analysis-modal-button--secondary"
            data-test="fertilizer-select-ai-analysis"
            onClick={selectAiAndClose}
            type="button"
          >
            Mit KI-Analyse fortfahren
          </button>
        </div>
      </div>
    </Modal>
  );
}
